/**
 * Purpose: Reorderable subject list for the "Pick Your Schedule" mode.
 * Owns: Displaying the 8 subjects with up/down arrow controls for reordering.
 * Notes: Final Exam is NOT shown — it's always period 9 automatically.
 *        All players share the same chosen schedule (no per-player customization).
 *        Uses inline styles matching the existing chalkboard aesthetic (CS-22).
 */

import type { Category } from '../../types/question';

interface SchedulePickerProps {
  /** Current ordered list of subjects */
  subjects: Category[];
  /** Called when the subject order changes */
  onReorder: (subjects: Category[]) => void;
}

/** Icons for each subject displayed next to the subject name (CS-20: no magic strings). */
const SUBJECT_ICONS: Record<Category, string> = {
  Math: '\u{1F9EE}',
  Science: '\u{1F52C}',
  History: '\u{1F4DC}',
  English: '\u{1F4D6}',
  Art: '\u{1F3A8}',
  Geography: '\u{1F30D}',
  Music: '\u{1F3B5}',
  Sports: '\u{1F3C8}',
};

export function SchedulePicker({ subjects, onReorder }: SchedulePickerProps) {
  /** Swap subject at `index` with the one above it. */
  function moveUp(index: number) {
    if (index <= 0) return;
    const updated = [...subjects];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onReorder(updated);
  }

  /** Swap subject at `index` with the one below it. */
  function moveDown(index: number) {
    if (index >= subjects.length - 1) return;
    const updated = [...subjects];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onReorder(updated);
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Class Order</h3>
      <p style={styles.hint}>Tap arrows to reorder your schedule</p>

      <div style={styles.list}>
        {subjects.map((subject, index) => (
          <div key={subject} style={styles.row}>
            {/* Period number */}
            <span style={styles.periodBadge}>{index + 1}</span>

            {/* Subject icon and name */}
            <span style={styles.subjectLabel}>
              {SUBJECT_ICONS[subject]} {subject}
            </span>

            {/* Reorder controls */}
            <div style={styles.arrows}>
              <button
                style={{
                  ...styles.arrowButton,
                  opacity: index === 0 ? 0.3 : 1,
                }}
                onClick={() => moveUp(index)}
                disabled={index === 0}
                aria-label={`Move ${subject} up`}
              >
                &#9650;
              </button>
              <button
                style={{
                  ...styles.arrowButton,
                  opacity: index === subjects.length - 1 ? 0.3 : 1,
                }}
                onClick={() => moveDown(index)}
                disabled={index === subjects.length - 1}
                aria-label={`Move ${subject} down`}
              >
                &#9660;
              </button>
            </div>
          </div>
        ))}

        {/* Final Exam always last — shown as fixed, not reorderable */}
        <div style={{ ...styles.row, ...styles.finalExamRow }}>
          <span style={styles.periodBadge}>9</span>
          <span style={styles.subjectLabel}>
            &#x1F393; Final Exam
          </span>
          <span style={styles.fixedLabel}>Always Last</span>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: '700',
    color: 'var(--text-light)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    margin: 0,
  },
  hint: {
    fontSize: '0.8rem',
    color: 'var(--muted)',
    margin: 0,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.6rem 0.75rem',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-surface)',
  },
  periodBadge: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: 'var(--accent-wood)',
    color: 'var(--text-light)',
    fontSize: '0.75rem',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  subjectLabel: {
    flex: 1,
    fontSize: '0.95rem',
    fontWeight: '600',
    color: 'var(--text-light)',
  },
  arrows: {
    display: 'flex',
    gap: '0.25rem',
  },
  arrowButton: {
    width: '36px',
    height: '36px',
    minWidth: '36px',
    minHeight: '36px',
    borderRadius: '6px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: 'var(--text-light)',
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  finalExamRow: {
    borderTop: '2px solid var(--accent-gold)',
    marginTop: '0.25rem',
    opacity: 0.8,
  },
  fixedLabel: {
    fontSize: '0.7rem',
    color: 'var(--muted)',
    fontStyle: 'italic',
  },
};
